import { useHotel } from "@/hooks/useHotel";
import type {
  ICreateStripeConnectPayload,
  ICreateStripeConnectResponse,
  IRefreshStripeConnectPayload,
  IUpdateHotelPaymentSettingsPayload,
  HotelPaymentMethodsResponse,
  StripeAccountRequirementsState,
  UpdateHotelPaymentMethodPayload,
  UpdateHotelPaymentMethodResponse,
} from "@/services/interfaces/IBillingSettingsService";
import { billingSettingsService } from "@/services/billing-service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { is404 } from "@/utils/httpErrors";
import { hotelKeys } from "@/components/pages/hotels/hooks/useHotels";
import type { Hotel, HotelsListResponse } from "@/types/hotel";
import { normalizeCurrency } from "../utils";

/**
 * Query keys
 */
export const billingSettingsKeys = {
  all: ["billingSettings"] as const,

  stripe: () => [...billingSettingsKeys.all, "stripe"] as const,
  stripeStatus: (hotelId: string) =>
    [...billingSettingsKeys.stripe(), "status", hotelId] as const,
  paymentMethods: (hotelId: string) =>
    [...billingSettingsKeys.all, "paymentMethods", hotelId] as const,
};

/**
 * GET: onboarding status
 */
export const useStripeConnectOnboardingStatus = () => {
  const { hotelId } = useHotel();

  return useQuery<StripeAccountRequirementsState | null>({
    queryKey: billingSettingsKeys.stripeStatus(hotelId || ""),
    enabled: !!hotelId,
    queryFn: async () => {
      try {
        return await billingSettingsService.getStripeConnectOnboardingStatus(
          hotelId!,
        );
      } catch (e) {
        if (is404(e)) return null;

        throw e;
      }
    },

    retry: (failureCount, error) => !is404(error) && failureCount < 2,
  });
};

/**
 * POST: create connect + get onboarding url
 */
export const useCreateStripeConnect = () => {
  const { hotelId } = useHotel();

  return useMutation<
    ICreateStripeConnectResponse,
    unknown,
    ICreateStripeConnectPayload
  >({
    mutationFn: (payload) => {
      if (!hotelId) throw new Error("hotelId is required");
      return billingSettingsService.createStripeConnect(hotelId, payload);
    },
  });
};

/**
 * POST: refresh connect + get new onboarding url
 */
export const useRefreshStripeConnect = () => {
  const { hotelId } = useHotel();

  return useMutation<
    ICreateStripeConnectResponse,
    unknown,
    IRefreshStripeConnectPayload
  >({
    mutationFn: (payload) => {
      if (!hotelId) throw new Error("hotelId is required");
      return billingSettingsService.refreshStripeConnect(hotelId, payload);
    },
  });
};

/**
 * POST: sync status (and usually you want to invalidate cached GET status)
 */
export const useSyncStripeAccountStatus = () => {
  const { hotelId } = useHotel();
  const qc = useQueryClient();

  return useMutation<StripeAccountRequirementsState, unknown, void>({
    mutationFn: async () => {
      if (!hotelId) throw new Error("hotelId is required");
      return billingSettingsService.syncStripeAccountStatus(hotelId);
    },
    onSuccess: () => {
      if (!hotelId) return;
      qc.invalidateQueries({
        queryKey: billingSettingsKeys.stripeStatus(hotelId),
      });
    },
  });
};

/**
 * PUT: update hotel payment settings (and invalidate status if your backend status depends on settings)
 */
export const useUpdateHotelPaymentSettings = () => {
  const { hotelId } = useHotel();
  const qc = useQueryClient();

  return useMutation<void, unknown, IUpdateHotelPaymentSettingsPayload>({
    mutationFn: async (payload) => {
      if (!hotelId) throw new Error("hotelId is required");
      return billingSettingsService.updateHotelPaymentSettings(
        hotelId,
        payload,
      );
    },
    onSuccess: (_, payload) => {
      if (!hotelId) return;
      const paymentSettings = {
        paymentEnabled: payload.paymentEnabled,
        currency: normalizeCurrency(payload.currency),
      };

      qc.setQueriesData<Hotel | null>({ queryKey: ["userHotel"] }, (current) =>
        current?.id === hotelId
          ? {
              ...current,
              currency: paymentSettings.currency,
              paymentSettings,
            }
          : current,
      );
      qc.setQueryData<Hotel | undefined>(
        hotelKeys.detail(hotelId),
        (current) =>
          current
            ? {
                ...current,
                currency: paymentSettings.currency,
                paymentSettings,
              }
            : current,
      );
      qc.setQueriesData<HotelsListResponse>(
        { queryKey: hotelKeys.lists() },
        (current) =>
          current
            ? {
                ...current,
                data: current.data.map((hotel) =>
                  hotel.id === hotelId
                    ? {
                        ...hotel,
                        currency: paymentSettings.currency,
                        paymentSettings,
                      }
                    : hotel,
                ),
              }
            : current,
      );
      qc.invalidateQueries({
        queryKey: billingSettingsKeys.stripeStatus(hotelId),
      });
      qc.invalidateQueries({ queryKey: ["userHotel"] });
      qc.invalidateQueries({ queryKey: hotelKeys.detail(hotelId) });
      qc.invalidateQueries({ queryKey: hotelKeys.lists() });
      qc.invalidateQueries({ queryKey: ["hotelServices"] });
      qc.invalidateQueries({ queryKey: ["services", hotelId] });
      qc.invalidateQueries({ queryKey: ["hotel", hotelId, "services"] });
    },
  });
};

export const useHotelPaymentMethods = (enabled = true) => {
  const { hotelId } = useHotel();

  return useQuery<HotelPaymentMethodsResponse>({
    queryKey: billingSettingsKeys.paymentMethods(hotelId || ""),
    enabled: enabled && !!hotelId,
    queryFn: async () => {
      if (!hotelId) throw new Error("hotelId is required");
      return billingSettingsService.getHotelPaymentMethods(hotelId);
    },
    retry: 1,
  });
};

export const useUpdateHotelPaymentMethod = () => {
  const { hotelId } = useHotel();
  const qc = useQueryClient();

  return useMutation<
    UpdateHotelPaymentMethodResponse,
    unknown,
    UpdateHotelPaymentMethodPayload
  >({
    mutationFn: async (payload) => {
      if (!hotelId) throw new Error("hotelId is required");
      return billingSettingsService.updateHotelPaymentMethod(hotelId, payload);
    },
    onSuccess: () => {
      if (!hotelId) return;
      qc.invalidateQueries({
        queryKey: billingSettingsKeys.paymentMethods(hotelId),
      });
    },
  });
};
